import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Fuel, Gauge, Calendar, Zap, RotateCcw, MessageCircle, Pin } from "lucide-react";
import { API, fmtPrice, fmtKm, waLink, SITE } from "@/lib/site";
import { CarDetailModal } from "@/components/CarDetailModal";

const TABS = ["Tutti", "Nuovo", "Km 0", "Usato Garantito"];
const BADGE = {
  "Nuovo": "bg-violet-500/15 text-violet-300 border-violet-500/30",
  "Km 0": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Usato Garantito": "bg-blue-500/15 text-blue-300 border-blue-500/30",
};

export const Catalog = ({ filters, setFilters }) => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetch(`${API}/cars`)
      .then((r) => r.json())
      .then((data) => setCars(Array.isArray(data) ? data : []))
      .catch(() => setCars([]))
      .finally(() => setLoading(false));
  }, []);

  const brands = useMemo(() => [...new Set(cars.map((c) => c.brand))].sort(), [cars]);
  const fuels = useMemo(() => [...new Set(cars.map((c) => c.fuel))].sort(), [cars]);

  const filtered = useMemo(() => {
    return cars.filter((c) => {
      if (filters.category !== "Tutti" && c.category !== filters.category) return false;
      if (filters.brand && c.brand !== filters.brand) return false;
      if (filters.fuel && c.fuel !== filters.fuel) return false;
      if (filters.maxPrice && c.price > Number(filters.maxPrice)) return false;
      if (filters.q) {
        const q = filters.q.toLowerCase();
        if (!`${c.brand} ${c.model}`.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [cars, filters]);

  const reset = () =>
    setFilters({ category: "Tutti", brand: "", fuel: "", maxPrice: "", q: "" });

  const sel =
    "bg-[#161D2B] border border-border rounded-xl px-4 py-2.5 text-sm text-gray-200 focus:outline-none focus:border-amber-500/60";

  return (
    <section id="catalogo" className="py-16 md:py-24 relative" data-testid="catalog-section">
      <div className="absolute inset-0 spotlight pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-10"
        >
          <p className="font-mono2 text-xs uppercase tracking-[0.35em] text-amber-500 mb-3">
            01 / Parco Auto
          </p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight">
            Il nostro parco auto
          </h2>
          <p className="mt-3 text-gray-400 max-w-2xl text-base">
            Usato garantito, Km 0 e nuove plurimarca: ogni veicolo è selezionato,
            controllato e pronto a scendere in strada.
          </p>
        </motion.div>

        <div className="flex flex-col gap-4 mb-8" data-testid="catalog-filters">
          <div className="flex flex-wrap gap-2">
            {TABS.map((t) => (
              <button
                key={t}
                data-testid={`catalog-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilters({ ...filters, category: t })}
                className={`px-5 py-2 rounded-full text-sm font-medium border transition-all ${
                  filters.category === t
                    ? "bg-amber-500 text-[#0A0D14] border-amber-500"
                    : "border-border text-gray-400 hover:border-amber-500/50 hover:text-gray-200"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <input
              data-testid="catalog-search-input"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Cerca modello…"
              className={`${sel} min-w-[180px] flex-1 sm:flex-none`}
            />
            <select
              data-testid="catalog-filter-brand"
              value={filters.brand || "all"}
              onChange={(e) => setFilters({ ...filters, brand: e.target.value === "all" ? "" : e.target.value })}
              className={sel}
            >
              <option value="all">Tutte le marche</option>
              {brands.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
            <select
              data-testid="catalog-filter-fuel"
              value={filters.fuel || "all"}
              onChange={(e) => setFilters({ ...filters, fuel: e.target.value === "all" ? "" : e.target.value })}
              className={sel}
            >
              <option value="all">Alimentazione</option>
              {fuels.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
            <select
              data-testid="catalog-filter-price"
              value={filters.maxPrice || "all"}
              onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value === "all" ? "" : e.target.value })}
              className={sel}
            >
              <option value="all">Prezzo max</option>
              <option value="20000">Fino a € 20.000</option>
              <option value="30000">Fino a € 30.000</option>
              <option value="50000">Fino a € 50.000</option>
              <option value="100000">Fino a € 100.000</option>
            </select>
            <button
              data-testid="catalog-reset-btn"
              onClick={reset}
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-amber-500 transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> Azzera
            </button>
          </div>
          <p className="font-mono2 text-xs uppercase tracking-widest text-gray-500" data-testid="catalog-count">
            {loading ? "Caricamento…" : `${filtered.length} veicoli disponibili`}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((car, i) => (
            <motion.article
              key={car.id}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
              whileHover={{ y: -6 }}
              className="group bg-[#161D2B] border border-border rounded-xl overflow-hidden hover:border-amber-500/40 transition-colors"
              data-testid={`car-card-${car.id}`}
            >
              <button
                data-testid={`car-card-open-${car.id}`}
                onClick={() => setSelected(car)}
                className="block w-full text-left"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={car.image}
                    alt={`${car.brand} ${car.model}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span
                    className={`absolute top-3 left-3 text-[11px] font-mono2 uppercase tracking-widest px-3 py-1 rounded-full border ${BADGE[car.category] || BADGE["Usato Garantito"]}`}
                    data-testid={`car-badge-${car.id}`}
                  >
                    {car.category}
                  </span>
                  {car.pinned && (
                    <span
                      className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-[11px] font-mono2 uppercase tracking-widest px-3 py-1 rounded-full bg-amber-500 text-[#0A0D14] border border-amber-400 shadow-lg shadow-amber-500/20"
                      data-testid={`car-pinned-badge-${car.id}`}
                    >
                      <Pin className="w-3 h-3" /> In evidenza
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <p className="font-mono2 text-[11px] uppercase tracking-widest text-amber-500">{car.brand}</p>
                  <h3 className="font-display text-lg sm:text-xl font-bold tracking-tight mt-1 leading-snug">
                    {car.model}
                  </h3>
                  <div className="mt-4 grid grid-cols-2 gap-y-2 text-xs text-gray-400">
                    <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-gray-500" /> {car.year}</span>
                    <span className="flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5 text-gray-500" /> {fmtKm(car.km)}</span>
                    <span className="flex items-center gap-1.5"><Fuel className="w-3.5 h-3.5 text-gray-500" /> {car.fuel}</span>
                    <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-gray-500" /> {car.power}</span>
                  </div>
                  <p className="mt-4 font-display text-2xl font-extrabold text-amber-500" data-testid={`car-price-${car.id}`}>
                    {fmtPrice(car.price)}
                  </p>
                </div>
              </button>
              <div className="px-5 pb-5 flex gap-2">
                <button
                  data-testid={`car-details-btn-${car.id}`}
                  onClick={() => setSelected(car)}
                  className="flex-1 bg-[#1E273A] hover:bg-amber-500 hover:text-[#0A0D14] text-sm font-medium py-2.5 rounded-lg transition-colors"
                >
                  Dettagli
                </button>
                <a
                  data-testid={`car-whatsapp-btn-${car.id}`}
                  href={waLink(`Buongiorno ${SITE.shortName}, sono interessato alla ${car.brand} ${car.model} (${fmtPrice(car.price)}). È ancora disponibile?`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500 hover:text-white text-sm font-medium px-3.5 py-2.5 rounded-lg transition-colors"
                  aria-label="Contatta su WhatsApp"
                >
                  <MessageCircle className="w-4 h-4" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>

        {!loading && filtered.length === 0 && (
          <div className="text-center py-20 text-gray-500" data-testid="catalog-empty">
            <p className="font-display text-2xl uppercase font-bold">Nessun veicolo trovato</p>
            <p className="mt-2 text-sm">Prova ad ampliare i filtri di ricerca.</p>
          </div>
        )}
      </div>

      <CarDetailModal car={selected} onClose={() => setSelected(null)} />
    </section>
  );
};
